<template>
  <div class="relative aspect-video w-full overflow-hidden" @click.stop>
    <TheIframe
      :src="contextVideoUrl"
      :title="videoTitle"
      :image-url="videoImageUrl"
      class="absolute left-0 top-0"
    />
  </div>
</template>

<script setup lang="ts">
  interface IProps {
    videoTitle: string;
    videoUrl: string;
    videoImageUrl?: string;
    isAutoPlay?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    isAutoPlay: false,
    videoImageUrl: '',
  });

  const {
    provideYoutubeEmbedPath,
    provideBilibiliEmbedPath,
    provideWistiaMediaEmbedPath,
  } = provideEmbedPath();

  const isYoutubeVideo = computed((): boolean => {
    return (
      props.videoUrl.includes('youtube') || props.videoUrl.includes('youtu.be')
    );
  });

  const isBilibiliVideo = computed((): boolean => {
    return props.videoUrl.includes('bilibili');
  });

  const isWistiaMediaVideo = computed((): boolean => {
    return props.videoUrl.includes('advantech.wistia.com');
  });

  const isBareWistiaCode = computed((): boolean => {
    return !props.videoUrl.includes('://') && !props.videoUrl.includes('/');
  });

  const appendAutoplay = (baseUrl: string, param: string): string => {
    if (!props.isAutoPlay) return baseUrl;
    return baseUrl.includes('?')
      ? `${baseUrl}&${param}`
      : `${baseUrl}?${param}`;
  };

  const contextVideoUrl = computed((): string => {
    if (isYoutubeVideo.value) {
      const baseUrl = provideYoutubeEmbedPath(props.videoUrl);
      return appendAutoplay(baseUrl, 'autoplay=1');
    }

    if (isBilibiliVideo.value) {
      const baseUrl = provideBilibiliEmbedPath(props.videoUrl);
      return appendAutoplay(baseUrl, 'autoplay=true');
    }

    if (isWistiaMediaVideo.value) {
      const baseUrl = provideWistiaMediaEmbedPath(props.videoUrl);
      return appendAutoplay(baseUrl, 'autoPlay=true');
    }

    if (isBareWistiaCode.value) {
      const baseUrl = `https://fast.wistia.net/embed/iframe/${props.videoUrl}`;
      return appendAutoplay(baseUrl, 'autoPlay=true');
    }

    return props.videoUrl;
  });
</script>
